/** Build watcher — stale open tabs pick up new deploys AUTOMATICALLY (Tom
 * 2026-07-24: "if daniel has one open it refreshes it automatically with a new
 * build right?").
 *
 * How: Cloudflare Pages fingerprints the main bundle (/assets/index-<hash>.js)
 * per build, so the hash referenced by "/" IS the deployed version. Every 5
 * minutes — and the moment a hidden tab becomes visible again — we re-fetch
 * "/" (cache: no-store, the push-only service worker has no fetch handler) and
 * compare. Different hash → this tab is running an old build:
 *   · hidden tab            → reload silently right now
 *   · visible tab, typing   → wait; retry on the next check (never eat input)
 *   · visible tab, idle     → 2s "Updating…" toast, then reload
 * The per-tab view + scroll restore (crm2_view / crm2_scroll) puts the user
 * back exactly where they were after the reload.
 *
 * Loop guard (rebuilt 2026-08-16 — Tom: Health "loads then gone after 2
 * seconds", every day, worst on deploy days): right after a deploy Cloudflare
 * edges can disagree about which build "/" is, and the old guard remembered
 * only the LAST hash it reloaded for — two edges serving A and B ping-ponged
 * the tab through an endless reload loop, felt as pages blanking ~2s after
 * they render (the toast + reload). Two rules now:
 *   1. HISTORY, not last-target: any hash this session has EVER reloaded for
 *      is banned — a ping-pong dies after one round trip, guaranteed.
 *   2. CONFIRMATION: a new hash must be seen on TWO CONSECUTIVE checks before
 *      we reload. One stale edge can't trigger anything; a real deploy is
 *      consistent and passes on the next tick. */

const RELOADED_FOR = "crm2_reloaded_for";

/** Hashes this session already reloaded for. Tolerates the pre-2026-08-16
 * format (a bare string) by treating it as a one-item history. */
export function reloadHistory(store: Pick<Storage, "getItem"> = sessionStorage): string[] {
  try {
    const raw = store.getItem(RELOADED_FOR);
    if (!raw) return [];
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [raw];
  } catch {
    try { const raw = store.getItem(RELOADED_FOR); return raw ? [raw] : []; } catch { return []; }
  }
}

/** Pure reload decision (unit-tested): given the freshly-fetched hash, the
 * running hash, the session's reload history and the last check's candidate,
 * say whether to reload and what the next candidate is. */
export function decideReload(
  next: string | null, cur: string | null, history: string[], candidate: string | null,
): { reload: boolean; candidate: string | null } {
  if (!next || !cur || next === cur) return { reload: false, candidate: null };
  if (history.includes(next)) return { reload: false, candidate: null };   // ping-pong ban
  if (candidate !== next) return { reload: false, candidate: next };       // 1st sighting arms
  return { reload: true, candidate: next };                                // 2nd in a row fires
}
const ASSET_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/;

/** The fingerprinted main-bundle path inside an index.html payload. */
export function extractIndexAsset(html: string): string | null {
  const m = ASSET_RE.exec(html);
  return m ? m[0] : null;
}

/** The bundle THIS tab is actually running (from its own <script> tag). */
export function currentIndexAsset(doc: Pick<Document, "querySelectorAll"> = document): string | null {
  for (const s of Array.from(doc.querySelectorAll("script[src]"))) {
    const m = ASSET_RE.exec(s.getAttribute("src") ?? "");
    if (m) return m[0];
  }
  return null;
}

/** True when reloading now would destroy in-progress input. */
function typing(): boolean {
  const a = document.activeElement;
  return a instanceof HTMLInputElement || a instanceof HTMLTextAreaElement ||
    (a instanceof HTMLElement && a.isContentEditable);
}

function toastThenReload(): void {
  try {
    const el = document.createElement("div");
    el.className = "buildtoast";
    el.textContent = "⟳ New version — updating…";
    document.body.appendChild(el);
    window.setTimeout(() => window.location.reload(), 2000);
  } catch {
    window.location.reload();
  }
}

/** Start watching. Returns a stop function (for tests; the app runs it for life). */
export function startBuildWatch(intervalMs = 5 * 60 * 1000): () => void {
  const cur = currentIndexAsset();
  if (!cur) return () => {}; // dev server / tests — nothing fingerprinted to compare
  let stopped = false;
  let reloading = false;
  let candidate: string | null = null;   // the hash awaiting its 2nd consecutive sighting

  const check = async (): Promise<void> => {
    if (stopped || reloading || !navigator.onLine) return;
    let next: string | null = null;
    try {
      const r = await fetch("/", { cache: "no-store", headers: { accept: "text/html" } });
      if (!r.ok) return;
      next = extractIndexAsset(await r.text());
    } catch { return; /* offline / transient — try again next tick */ }
    const d = decideReload(next, cur, reloadHistory(), candidate);
    candidate = d.candidate;
    if (!d.reload) return;
    if (document.visibilityState === "visible" && typing()) return; // never eat what they're typing
    try {
      sessionStorage.setItem(RELOADED_FOR, JSON.stringify([...reloadHistory(), next]));
    } catch { /* private mode — worst case the confirmation rule still holds */ }
    reloading = true;
    if (document.visibilityState === "hidden") window.location.reload();
    else toastThenReload();
  };

  const timer = window.setInterval(() => { void check(); }, intervalMs);
  const kick = window.setTimeout(() => { void check(); }, 45_000); // first look shortly after load
  const onVis = () => { if (document.visibilityState === "visible") void check(); };
  document.addEventListener("visibilitychange", onVis);
  return () => {
    stopped = true;
    window.clearInterval(timer);
    window.clearTimeout(kick);
    document.removeEventListener("visibilitychange", onVis);
  };
}
